/**
 * Hook do ładowania szablonu Excel z API przy starcie aplikacji
 * Używa CarboWasteApi.loadTemplateFromAPI() do pobrania danych
 * Szablon jest ładowany tylko raz per sesja - przy ponownym uruchomieniu dodatku
 */

import { useState, useCallback, useRef } from 'react';
import { carboWasteClient } from '../../services/ApiClients';

interface UseTemplateLoaderResult {
    isLoading: boolean;
    error: string | null;
    isLoaded: boolean;
    loadTemplate: () => Promise<void>;
}

/**
 * Klucz w sessionStorage do śledzenia czy szablon już został załadowany w tej sesji
 */
const TEMPLATE_LOADED_KEY = 'carbowaste_template_loaded_session';

/**
 * Hook do zarządzania procesem ładowania szablonu
 * Szablon jest ładowany tylko raz per sesja (uruchomienie dodatku)
 */
export const useTemplateLoader = (): UseTemplateLoaderResult => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isLoaded, setIsLoaded] = useState(false);

    // Użyj ref do śledzenia czy ładowanie jest w toku (bardziej niezawodne niż state)
    const isLoadingRef = useRef(false);

    const loadTemplate = useCallback(async () => {
        console.log('🔍 [useTemplateLoader] loadTemplate called', {
            isLoading: isLoadingRef.current,
            isLoaded,
            sessionStorage: sessionStorage.getItem(TEMPLATE_LOADED_KEY)
        });

        // Sprawdź czy szablon był już załadowany w tej sesji
        const wasLoadedInSession = sessionStorage.getItem(TEMPLATE_LOADED_KEY);
        if (wasLoadedInSession === 'true') {
            console.log('📋 Template already loaded in this session, skipping...');
            setIsLoaded(true);
            return;
        }

        // Jeśli szablon już załadowany w tym komponencie, nie ładuj ponownie
        if (isLoaded) {
            console.log('📋 Template already loaded, skipping...');
            return;
        }

        // Jeśli trwa ładowanie, nie uruchamiaj ponownie (używamy ref dla natychmiastowej synchroniczności)
        if (isLoadingRef.current) {
            console.log('⏳ Template loading already in progress (ref check), skipping...');
            return;
        }

        console.log('📥 Starting template load from API...');
        isLoadingRef.current = true;
        setIsLoading(true);
        setError(null);

        try {
            // Wywołaj metodę z carboWasteClient
            console.log('🔧 Calling carboWasteClient.loadTemplate()...');
            await carboWasteClient.loadTemplate();

            console.log('✅ Template loaded successfully');

            // Zapisz w sessionStorage że szablon został załadowany w tej sesji
            sessionStorage.setItem(TEMPLATE_LOADED_KEY, 'true');
            console.log('💾 Saved template loaded flag to sessionStorage');

            setIsLoaded(true);
            setError(null);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error loading template';
            console.error('❌ Template loading failed:', errorMessage, err);
            setError(errorMessage);
            setIsLoaded(false);
        } finally {
            isLoadingRef.current = false;
            setIsLoading(false);
        }
    }, [isLoaded]); // Usuń isLoading z dependencies - używamy ref

    return {
        isLoading,
        error,
        isLoaded,
        loadTemplate
    };
};
